import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BlockBlobClient } from '@azure/storage-blob';
import { saveAs } from 'file-saver';
import { concat, map, mergeMap, Observable, of, switchMap, timeout, zip } from 'rxjs';

import { PlatformConfigService } from '../../../features/config/config.service';
import { STORAGE_API_ENDPOINTS } from '../../../features/config/config.type';
import { StorageFile } from '../file/file.type';
import {
  StorageApiSignedUrlResponse,
  StorageDownloadProgress,
  StorageDownloadUrl,
} from './download.type';

@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  // Constants
  readonly #downloadTimeout = 300_000; // 5 minutes
  readonly #downloadRetries = 3;

  // Services
  readonly #configService = inject(PlatformConfigService);
  readonly #httpClient = inject(HttpClient);
  readonly #destroyRef = inject(DestroyRef);

  // Public methods
  /** Fetches a temporary signed SAS URL for downloading a file.
   * @param fileId The ID of the file to retrieve a download URL for.
   * @param options.inline When true, generates a URL with Content-Disposition: inline for browser preview.
   */
  downloadUrl$(fileId: string, options?: { inline?: boolean }): Observable<StorageDownloadUrl> {
    return this.#configService
      .safelyGetApiUrl$('STORAGE', STORAGE_API_ENDPOINTS.transfer.download.signedUrl)
      .pipe(
        switchMap((uri) => {
          const params: Record<string, string> = { fileId };
          if (options?.inline) params['inline'] = 'true';
          return this.#httpClient.post<StorageApiSignedUrlResponse>(uri, null, { params });
        }),
        timeout(30000), // 30 second timeout for getting URL
        map(({ result }) => ({
          fileId,
          sasUri: result.sasUri,
          startsOn: new Date(),
          expiresOn: new Date(result.expiration),
        })),
        takeUntilDestroyed(this.#destroyRef),
      );
  }

  /** Downloads multiple files with streaming progress using the Azure Blob SDK, saving each file to disk.
   * Files are downloaded sequentially to avoid overwhelming the browser.
   * @param files List of files to download, each with an `id` and `name`.
   */
  downloadFiles$(files: { name: string; id: string }[]): Observable<StorageDownloadProgress> {
    const downloadUrls$ = files.map((file) => this.downloadUrl$(file.id));

    return zip(downloadUrls$).pipe(
      mergeMap((result) => {
        const urlMap = new Map(result.map((r) => [r.fileId, r.sasUri]));

        // Download each file sequentially to avoid overwhelming the browser
        return concat(
          ...files.map((file) => {
            const url = urlMap.get(file.id);

            if (!url) {
              return of({
                file,
                percentage: 0,
                done: true,
                error: new Error('No download URL available'),
              });
            }

            return this.#downloadWithBlobClient(url, file);
          }),
        );
      }),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  /** Triggers browser-native anchor-click downloads for multiple files.
   * Signed URLs are resolved in parallel, then each download is initiated with a 1-second delay between them.
   * @param files List of files to download, each with an `id` and `name`.
   */
  downloadItemsByUrl$(files: Pick<StorageFile, 'id' | 'name'>[]): Observable<void> {
    const downloadUrls$ = files.map((item) => this.downloadUrl$(item.id));

    return zip(downloadUrls$).pipe(
      timeout(30000), // 30 second timeout for getting all URLs
      map((result) => {
        const urlMap = new Map(result.map((r) => [r.fileId, r.sasUri]));

        // Download each file with a small delay between them
        files.forEach((item, index) => {
          setTimeout(() => {
            const url = urlMap.get(item.id);
            if (url) {
              this.#downloadOnBrowser(url, item.name);
            }
          }, index * 1000); // 1 second delay between each download
        });
      }),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  // Private methods
  #downloadWithBlobClient(
    url: string,
    file: Pick<StorageFile, 'id' | 'name'>,
  ): Observable<StorageDownloadProgress> {
    const blobClient = new BlockBlobClient(url);
    let retryCount = 0;

    return new Observable((observer) => {
      const downloadToBuffer = async () => {
        try {
          const response = await blobClient.download(0);
          const contentLength = Number(response.contentLength);
          let downloadedBytes = 0;
          const chunks: Uint8Array[] = [];

          // Use the browser's fetch API to handle the download stream
          const res = await fetch(url);
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          const reader = res.body?.getReader();

          if (!reader) {
            throw new Error('No readable stream available');
          }

          while (true) {
            const { done, value } = await reader.read();

            if (done) break;

            chunks.push(value);
            downloadedBytes += value.length;

            const percentage = Math.round((downloadedBytes * 100) / contentLength);
            observer.next({ file, percentage, done: false });
          }

          const blob = new Blob(chunks as BlobPart[]);
          saveAs(blob, file.name);

          observer.next({ file, percentage: 100, done: true });
          observer.complete();
        } catch (error) {
          if (retryCount < this.#downloadRetries) {
            retryCount++;

            await new Promise((resolve) => setTimeout(resolve, 1000 * retryCount));

            return downloadToBuffer();
          }

          observer.error({ error, file });
        }
      };

      downloadToBuffer();

      const dlTimeout = setTimeout(() => {
        observer.error(new Error('Download timeout'));
      }, this.#downloadTimeout);

      return () => {
        clearTimeout(dlTimeout);
      };
    });
  }

  async #downloadOnBrowser(url: string, name: string): Promise<void> {
    const link: HTMLAnchorElement = document.createElement('a');
    link.href = url;
    link.download = encodeURIComponent(name);

    // Test if the URL is accessible
    const response = await fetch(url, { method: 'HEAD' });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    link.click();
  }
}
